import React, { useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { Checkbox, Input, NumericTextBox, Slider, Switch } from '@progress/kendo-react-inputs';
import { MultiSelect, DropDownList } from '@progress/kendo-react-dropdowns';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { fetchApi } from '../../services/api';
import MultipleCustomerInput, { CustomerInputValue } from '../../components/MultipleCustomerInput';
import { QuoteRuleViewModel } from 'TypeGen/Quote/Rules/quote-rule-view-model';
import { QuoteRuleActions } from 'TypeGen/Quote/Rules/quote-rule-actions';
import { QuoteRuleDimension } from 'TypeGen/Quote/Rules/quote-rule-dimension';
import { FrontInboxViewModel } from 'TypeGen/Quote/Rules/front-inbox-view-model';
import { JsonResponse } from 'TypeGen/json-response';
import { IDNameViewModel } from 'TypeGen/id-name-view-model';
import { filterBy, FilterDescriptor } from '@progress/kendo-data-query';

type Props = {
  rule: QuoteRuleViewModel | null;   // null = new
  sources: IDNameViewModel[];
  vehicleTypes: IDNameViewModel[];
  frontInboxes: FrontInboxViewModel[];
  onClose: () => void;
  onSaved: () => void;
};

const defaultActions: QuoteRuleActions = {
  DefaultVehicleReserveMinutes: 15,
  DefaultMaxPickupHoursOut: 24,
  DefaultPriceWinWeight: 0.70,
  DefaultPriceMinMargin: 0.20,
  AutoCreate: true,
  UsePriceOptimization: false,
  PriceWinWeightUnder500Miles: null, PriceWinWeight500PlusMiles: null, PriceMinMargin: null,
  AutoOffer: false, AutoOfferWorkHours: false, AutoOfferAfterHours: false,
  AfterHoursFrom: '18:00:00', AfterHoursTo: '06:00:00',
  AutoSubmitBid: false,
  BlindBidAsset: false, BlindBidMinPAccept: null, BlindBidMinVehiclesInArea: null,
  BlindBidCarrier: false, BlindBidMinCarrierVehiclesInArea: null,
  PostToCarrierLoadBoard: false, AutoDecline: false,
  UnitsInAreaMiles: null, AutoOfferMiles: null, VehicleReserveMinutes: null,
  MaxPickupHoursOut: null,
};

// Seed values when a nullable override is first enabled (null itself = system default / dynamic).
const OVERRIDE_SEED = { UnitsInAreaMiles: 300, AutoOfferMiles: 125, VehicleReserveMinutes: 15, MaxPickupHoursOut: 24 };

// System defaults the price recommendation uses when a rule leaves these null (QuoteRuleActions.DefaultPrice*).
const PRICE_DEFAULTS = { WinWeight: 0.70, MinMargin: 0.20 };
const autoOfferRadiusDetails = '50 mi when loaded miles <= 100; 75 mi when loaded miles <= 375; otherwise 20% of loaded miles, capped at 125 mi.';

// Keep time options aligned with the legacy quote-automation after-hours windows.
const afterHoursFrom = [
  { Value: '13:00:00', Name: '1pm' }, { Value: '14:00:00', Name: '2pm' }, { Value: '15:00:00', Name: '3pm' },
  { Value: '16:00:00', Name: '4pm' }, { Value: '17:00:00', Name: '5pm' }, { Value: '18:00:00', Name: '6pm' },
  { Value: '19:00:00', Name: '7pm' }, { Value: '20:00:00', Name: '8pm' }, { Value: '21:00:00', Name: '9pm' },
  { Value: '22:00:00', Name: '10pm' }, { Value: '23:00:00', Name: '11pm' }, { Value: '00:00:00', Name: '12am' },
];
const afterHoursTo = [
  { Value: '01:00:00', Name: '1am' }, { Value: '02:00:00', Name: '2am' }, { Value: '03:00:00', Name: '3am' },
  { Value: '04:00:00', Name: '4am' }, { Value: '05:00:00', Name: '5am' }, { Value: '06:00:00', Name: '6am' },
  { Value: '07:00:00', Name: '7am' }, { Value: '08:00:00', Name: '8am' }, { Value: '09:00:00', Name: '9am' },
  { Value: '10:00:00', Name: '10am' }, { Value: '11:00:00', Name: '11am' }, { Value: '12:00:00', Name: '12pm' },
];

const sectionHead = (tag: string, color: string) => (
  <div className="mb-2">
    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color, background: color + '22', padding: '2px 8px', borderRadius: 5 }}>{tag}</span>
  </div>
);

// Muted uppercase subheader for grouping the Then actions (Intake / Coverage / Fallback).
const groupHead = (text: string, first = false) => (
  <div className={`text-muted ${first ? '' : 'mt-4'} mb-1`}
    style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase' }}>{text}</div>
);

const RuleEditor: React.FC<Props> = ({ rule, sources, vehicleTypes, frontInboxes, onClose, onSaved }) => {
  const isDefault = rule?.IsDefault ?? false;

  const findValues = (dim: QuoteRuleDimension) =>
    rule?.Conditions.find(c => c.Dimension === dim)?.Values ?? [];

  const [name, setName] = useState(rule?.Name ?? '');
  const enabled = rule?.Enabled ?? true;
  const [actions, setActions] = useState<QuoteRuleActions>(rule ? { ...rule.Actions } : { ...defaultActions });
  const [saving, setSaving] = useState(false);

  const [sourceVals, setSourceVals] = useState<IDNameViewModel[]>(findValues(QuoteRuleDimension.Source).map(v => sources.find(s => String(s.ID) === v.Value) ?? { ID: +v.Value, Name: v.Label }));
  const [sourceValsFilter, setSourceValsFilter] = useState<FilterDescriptor>();
  const [vehicleVals, setVehicleVals] = useState<IDNameViewModel[]>(findValues(QuoteRuleDimension.VehicleType).map(v => vehicleTypes.find(s => String(s.ID) === v.Value) ?? { ID: +v.Value, Name: v.Label }));
  const [customerVals, setCustomerVals] = useState<CustomerInputValue[]>(findValues(QuoteRuleDimension.BillingCustomer).map(v => ({ CustomerID: +v.Value, CustomerNumber: v.Label })));
  const [inboxVals, setInboxVals] = useState<FrontInboxViewModel[]>(findValues(QuoteRuleDimension.FrontInbox).map(v => frontInboxes.find(i => i.ID === v.Value) ?? { ID: v.Value, Name: v.Label }));
  const [inboxValsFilter, setInboxValsFilter] = useState<FilterDescriptor>();

  const setAction = (patch: Partial<QuoteRuleActions>) => setActions(prev => ({ ...prev, ...patch }));

  const save = () => {
    if (!isDefault && !name.trim()) { alert('Please enter a rule name.'); return; }
    if (actions.BlindBidAsset && actions.BlindBidMinPAccept == null && actions.BlindBidMinVehiclesInArea == null) {
      alert('Blind Bid Asset needs at least one signal to be checked. Please set a threshold or turn off Asset.');
      return;
    }
    setSaving(true);

    const conditions: { Dimension: QuoteRuleDimension; Values: string[] }[] = [];
    if (!isDefault) {
      if (sourceVals.length) conditions.push({ Dimension: QuoteRuleDimension.Source, Values: sourceVals.map(s => String(s.ID)) });
      if (customerVals.length) conditions.push({ Dimension: QuoteRuleDimension.BillingCustomer, Values: customerVals.map(c => String(c.CustomerID)) });
      if (vehicleVals.length) conditions.push({ Dimension: QuoteRuleDimension.VehicleType, Values: vehicleVals.map(v => String(v.ID)) });
      if (inboxVals.length) conditions.push({ Dimension: QuoteRuleDimension.FrontInbox, Values: inboxVals.map(i => i.ID) });
    }

    const command = {
      ID: rule?.ID ?? 0,
      Name: name,
      Enabled: enabled,
      Conditions: conditions,
      Actions: {
        ...actions,
        AutoSubmitBid: actions.AutoSubmitBid,
        BlindBidMinPAccept: actions.BlindBidAsset ? actions.BlindBidMinPAccept : null,
        BlindBidMinVehiclesInArea: actions.BlindBidAsset ? actions.BlindBidMinVehiclesInArea : null,
        BlindBidMinCarrierVehiclesInArea: actions.BlindBidCarrier ? actions.BlindBidMinCarrierVehiclesInArea : null,
      },
      RowVersion: rule?.RowVersion ?? null,
    };

    fetchApi('/api/QuoteRules/Save', command, 'post')
      .then((r: JsonResponse) => r.Success ? onSaved() : alert(r.ErrorMessage))
      .catch((e) => { if (!e?.status) alert('Unable to save rule'); })
      .finally(() => setSaving(false));
  };

  return (
    <Dialog title={rule ? (isDefault ? 'Default (fallback) rule' : 'Edit rule') : 'New rule'} onClose={onClose} className='dialog-w800'>
      {!isDefault && (
        <div className="k-form-field" style={{ marginBottom: 16 }}>
          <label className="text-muted small" style={{ fontWeight: 600 }}>Rule name</label>
          <Input value={name} onChange={(e) => setName(e.value as string)}
            placeholder="e.g. Rivian cargo vans — blind bid" />
        </div>
      )}

      {/* IF */}
      {!isDefault && (
        <div className="mb-4">
          {sectionHead('If', '#1355A2')}
          <div>
            <div className="k-form-field" style={{ marginBottom: 12 }}>
              <label className="text-muted small" style={{ fontWeight: 600 }}>Quote source</label>
              <MultiSelect
                data={filterBy(sources, sourceValsFilter)}
                filterable
                onFilterChange={(e) => setSourceValsFilter(e.filter)}
                placeholder="Any source"
                textField="Name"
                dataItemKey="ID"
                value={sourceVals}
                onChange={(e) => setSourceVals(e.value)}
              />
            </div>
            <div className="k-form-field" style={{ marginBottom: 12 }}>
              <label className="text-muted small" style={{ fontWeight: 600 }}>Billing customer</label>
              <MultipleCustomerInput value={customerVals} onChange={setCustomerVals} placeholder="Any customer" fullWidth />
            </div>
            <div className="k-form-field" style={{ marginBottom: 12 }}>
              <label className="text-muted small" style={{ fontWeight: 600 }}>Vehicle type</label>
              <MultiSelect data={vehicleTypes} textField="Name" dataItemKey="ID"
                value={vehicleVals} onChange={(e) => setVehicleVals(e.value)} placeholder="Any vehicle type" />
            </div>
            <div className="k-form-field">
              <label className="text-muted small" style={{ fontWeight: 600 }}>Front inbox</label>
              <MultiSelect
                data={filterBy(frontInboxes, inboxValsFilter)}
                filterable
                onFilterChange={(e) => setInboxValsFilter(e.filter)}
                textField="Name"
                dataItemKey="ID"
                value={inboxVals}
                onChange={(e) => setInboxVals(e.value)}
                placeholder="Any inbox"
              />
            </div>
          </div>
        </div>
      )}

      <div>
        <div>
          {sectionHead('Then', '#1F7A4D')}
          {groupHead('Intake', true)}
          <ActionToggle label="Auto create the quote" desc="Parse the request and create a quote."
            checked={actions.AutoCreate} onChange={(v) => setAction({ AutoCreate: v })} />
          <ActionToggle label="Adjust rate with Dynamic Price Optimizer" desc="Adjust the rate using our recommended price."
            checked={actions.UsePriceOptimization} onChange={(v) => setAction({ UsePriceOptimization: v })} last />
          {actions.UsePriceOptimization && (
            <div className="border rounded p-3 mb-2">
              <div style={{ fontWeight: 600 }}>Weighted score bias</div>
              <div className="text-muted small mb-2">Balance the recommendation between Win % and Margin $.</div>
              <WinWeightSlider label="< 500 miles" value={actions.PriceWinWeightUnder500Miles ?? PRICE_DEFAULTS.WinWeight}
                onChange={(v) => setAction({ PriceWinWeightUnder500Miles: v })} />
              <WinWeightSlider label="≥ 500 miles" value={actions.PriceWinWeight500PlusMiles ?? PRICE_DEFAULTS.WinWeight}
                onChange={(v) => setAction({ PriceWinWeight500PlusMiles: v })} />

              <div className="border-top mt-3 pt-3">
                <div style={{ fontWeight: 600 }}>Minimum margin</div>
                <div className="text-muted small mb-2">Exclude prices below this margin over driver or carrier cost.</div>
                <div className="small text-center">{Math.round((actions.PriceMinMargin ?? PRICE_DEFAULTS.MinMargin) * 100)}% minimum margin</div>
                <Slider className="w-100" buttons={false} min={0} max={50} step={1}
                  value={Math.round((actions.PriceMinMargin ?? PRICE_DEFAULTS.MinMargin) * 100)}
                  onChange={(e) => setAction({ PriceMinMargin: Math.round(e.value) / 100 })} />
                <div className="d-flex justify-content-between text-muted small"><span>0%</span><span>50%</span></div>
              </div>
            </div>
          )}

          {groupHead('Coverage')}
          <div className="text-muted small mb-1">How the rule tries to win the load, attempted in this order.</div>

          <div className="border-bottom" style={{ padding: '12px 0' }}>
            <div style={{ fontWeight: 600 }}>Blind bid</div>
            <div className="text-muted small mb-2">
              Place a bid automatically when confident in capacity{actions.AutoOffer ? ', before offering to drivers' : ''}.
              {actions.BlindBidAsset && actions.BlindBidCarrier ? ' Bids when either asset or carrier signalrs clear their thresholds.' : ''}
            </div>

            <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start', marginBottom: 12 }}>
              <Switch size="small" checked={actions.BlindBidAsset}
                onChange={(e) => setAction(e.value && actions.BlindBidMinPAccept == null && actions.BlindBidMinVehiclesInArea == null
                  ? { BlindBidAsset: true, BlindBidMinPAccept: 0.90, BlindBidMinVehiclesInArea: 3 }
                  : { BlindBidAsset: e.value })} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>Asset</div>
                <div className="text-muted small">Bid off our own fleet when {actions.BlindBidMinPAccept != null && actions.BlindBidMinVehiclesInArea != null ? 'both ' : ''}these signals clear their threshold.</div>

                {actions.BlindBidAsset &&
                  <div className="border rounded p-3 mt-2">
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                      <Checkbox checked={actions.BlindBidMinPAccept != null}
                        onChange={(e) => setAction({ BlindBidMinPAccept: e.value ? (actions.BlindBidMinPAccept ?? 0.90) : null })}
                        label="Overall P(Accept) ≥" />
                      {actions.BlindBidMinPAccept != null &&
                        <NumericTextBox width={90} min={0} max={100} step={1} format="n0"
                          value={Math.round((actions.BlindBidMinPAccept ?? 0) * 100)}
                          onChange={(e) => setAction({ BlindBidMinPAccept: (e.value ?? 0) / 100 })} />}
                      {actions.BlindBidMinPAccept != null && <span className="text-muted">%</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <Checkbox checked={actions.BlindBidMinVehiclesInArea != null}
                        onChange={(e) => setAction({ BlindBidMinVehiclesInArea: e.value ? (actions.BlindBidMinVehiclesInArea ?? 3) : null })}
                        label="Fleet vehicles in area ≥" />
                      {actions.BlindBidMinVehiclesInArea != null &&
                        <NumericTextBox width={90} min={1} step={1} format="n0"
                          value={actions.BlindBidMinVehiclesInArea}
                          onChange={(e) => setAction({ BlindBidMinVehiclesInArea: e.value })} />}
                    </div>
                    {actions.BlindBidMinPAccept == null && actions.BlindBidMinVehiclesInArea == null &&
                      <div className="text-danger small mt-2">Check at least one signal, or turn Asset off.</div>}
                  </div>}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
              <Switch size="small" checked={actions.BlindBidCarrier}
                onChange={(e) => setAction({
                  BlindBidCarrier: e.value,
                  BlindBidMinCarrierVehiclesInArea: e.value ? (actions.BlindBidMinCarrierVehiclesInArea ?? 15) : actions.BlindBidMinCarrierVehiclesInArea,
                })} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>Carrier</div>
                <div className="text-muted small">Bid off outside carrier capacity in the area.</div>

                {actions.BlindBidCarrier &&
                  <div className="border rounded p-3 mt-2" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span>Carrier vehicles in area ≥</span>
                    <NumericTextBox width={90} min={1} step={1} format="n0"
                      value={actions.BlindBidMinCarrierVehiclesInArea ?? 0}
                      onChange={(e) => setAction({ BlindBidMinCarrierVehiclesInArea: e.value })} />
                  </div>}
              </div>
            </div>
          </div>

          {/* Offer to fleet */}
          <div className="border-bottom" style={{ padding: '12px 0' }}>
            <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
              <Switch size="small" checked={actions.AutoOffer}
                onChange={(e) => {
                  if (e.value && !actions.AutoOfferWorkHours && !actions.AutoOfferAfterHours)
                    setAction({ AutoOffer: true, AutoOfferWorkHours: true, AutoOfferAfterHours: true });
                  else setAction({ AutoOffer: e.value });
                }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>Offer to fleet</div>
                <div className="text-muted small">Offer to eligible drivers during the selected windows.</div>

                {actions.AutoOffer && (
                  <div className="border rounded p-3 mt-2">
                    <Checkbox checked={actions.AutoOfferWorkHours}
                      onChange={(e) => setAction({ AutoOfferWorkHours: e.value })} label="During office hours" />
                    <Checkbox checked={actions.AutoOfferAfterHours}
                      onChange={(e) => setAction({ AutoOfferAfterHours: e.value })} label="After hours" />
                    {actions.AutoOfferWorkHours !== actions.AutoOfferAfterHours && (
                      <div className="d-flex align-items-end mt-3" style={{ gap: 12 }}>
                        <div className="k-form-field mb-0">
                          <label className="text-muted small">After hours from</label>
                          <DropDownList textField="Name" data={afterHoursFrom}
                            value={afterHoursFrom.find(x => x.Value === actions.AfterHoursFrom) ?? null}
                            onChange={(e) => setAction({ AfterHoursFrom: e.target.value.Value })} />
                        </div>
                        <div className="k-form-field mb-0">
                          <label className="text-muted small">to</label>
                          <DropDownList textField="Name" data={afterHoursTo}
                            value={afterHoursTo.find(x => x.Value === actions.AfterHoursTo) ?? null}
                            onChange={(e) => setAction({ AfterHoursTo: e.target.value.Value })} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <ActionToggle label="Post to carrier load board"
            desc={actions.AutoOffer || actions.BlindBidAsset || actions.BlindBidCarrier
              ? "Post the load to the carrier load board as a fallback when the fleet doesn't take it or there's no vehicle in the area."
              : "Post the load to the carrier load board to source an outside carrier bid."}
            checked={actions.PostToCarrierLoadBoard} onChange={(v) => setAction({ PostToCarrierLoadBoard: v })} />

          <ActionToggle label="Auto bid"
            desc="When a driver accepts or a carrier bids, send the rate to the customer automatically"
            checked={actions.AutoSubmitBid} onChange={(v) => setAction({ AutoSubmitBid: v })} />

          {groupHead('Fallback')}
          <ActionToggle label="Auto decline" desc="When nothing above covers the load, send the templated “no units available” reply and archive the thread."
            checked={actions.AutoDecline} onChange={(v) => setAction({ AutoDecline: v })} last />

          {groupHead('Setting overrides')}
          <div className="text-muted small mb-1">You can override these defaults by inputting a different value.</div>
          <div className="border rounded p-3">
            <OverrideRow label="Units-in-area radius" hint="300 miles" suffix="mi"
              min={25} max={500} step={25} seed={OVERRIDE_SEED.UnitsInAreaMiles}
              value={actions.UnitsInAreaMiles} onChange={(v) => setAction({ UnitsInAreaMiles: v })} />
            <OverrideRow label="Auto offer / blind-bid radius" hint="dynamic by distance" hintInfo={autoOfferRadiusDetails} suffix="mi"
              min={25} max={500} step={25} seed={OVERRIDE_SEED.AutoOfferMiles}
              value={actions.AutoOfferMiles} onChange={(v) => setAction({ AutoOfferMiles: v })} />
            <OverrideRow label="Vehicle reserve time" hint="15 min" suffix="min"
              min={0} max={60} step={5} seed={OVERRIDE_SEED.VehicleReserveMinutes}
              value={actions.VehicleReserveMinutes} onChange={(v) => setAction({ VehicleReserveMinutes: v })} />
            <OverrideRow label="Skip coverage if pickup more than" hint="24 hr" suffix="hr"
              min={1} max={1000} step={1} seed={OVERRIDE_SEED.MaxPickupHoursOut}
              value={actions.MaxPickupHoursOut} onChange={(v) => setAction({ MaxPickupHoursOut: v })} last />
          </div>
        </div>
      </div>
      <DialogActionsBar layout="end">
        <Button onClick={onClose}>Cancel</Button>
        <Button themeColor="primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save rule'}</Button>
      </DialogActionsBar>
    </Dialog>
  );
};

const ActionToggle: React.FC<{ label: string; desc: string; checked: boolean; onChange: (v: boolean) => void; last?: boolean }> =
  ({ label, desc, checked, onChange, last }) => (
    <div className={`d-flex align-items-start${last ? '' : ' border-bottom'}`} style={{ gap: 11, padding: '12px 0' }}>
      <Switch size="small" checked={checked} onChange={(e) => onChange(e.value)} />
      <div>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div className="text-muted small">{desc}</div>
      </div>
    </div>
  );

// Same win % vs margin $ slider as the Quote page's Pricing Recommendation.
const WinWeightSlider: React.FC<{ label: string; value: number; onChange: (v: number) => void }> = ({ label, value, onChange }) => (
  <div className="mt-2">
    <div className="small" style={{ fontWeight: 600 }}>{label}</div>
    <div className="small d-flex justify-content-between">
      <span>Win % {Math.round(value * 100)}%</span>
      <span>Margin $ {100 - Math.round(value * 100)}%</span>
    </div>
    <Slider className="w-100" buttons={false} min={0} max={100} step={5}
      value={Math.round(value * 100)}
      onChange={(e) => onChange(Math.round(e.value / 5) * 5 / 100)} />
  </div>
);

// A nullable numeric override: unchecked = system default (shown as a hint), checked = explicit value.
const OverrideRow: React.FC<{
  label: string; hint: string; hintInfo?: string; suffix: string; min: number; max: number; step: number;
  value: number | null; seed: number; onChange: (v: number | null) => void; last?: boolean;
}> = ({ label, hint, hintInfo, suffix, min, max, step, value, seed, onChange, last }) => (
  <div className={`d-flex align-items-center${last ? '' : ' mb-2'}`} style={{ gap: 10 }}>
    <Checkbox checked={value != null} label={label}
      onChange={(e) => onChange(e.value ? (value ?? seed) : null)} />
    {value != null
      ? <><NumericTextBox width={90} min={min} max={max} step={step} format="n0"
          value={value} onChange={(e) => onChange(e.value)} /><span className="text-muted">{suffix}</span></>
      : hintInfo ? <Tooltip anchorElement="target" parentTitle={true} openDelay={250} position="top">
          <span title={hintInfo} className="text-muted small" style={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>{hint}</span>
        </Tooltip> : <span className="text-muted small">{hint}</span>}
  </div>
);

export default RuleEditor;
